import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AssetDto, GalleryDto, Tag, TenantDto } from '@nusign/picflow-backend-types';
import { combineLatest, distinctUntilKeyChanged, filter, switchMap, take, tap } from 'rxjs';
import { EditTagDialogComponent } from '../../../gallery/dialogs/edit-tag-dialog/edit-tag-dialog.component';
import { AssetTagService } from '../../../services/asset-tag.service';
import { AssetService } from '../../../services/asset.service';
import { DialogService } from '../../../services/dialog.service';
import { PermissionService } from '../../../services/permission.service';
import { StateService } from '../../../services/state.service';
import { TagService } from '../../../services/tag.service';
import { AssetTagListComponent } from '../asset-tag-list/asset-tag-list.component';
import { AssetTagSelectComponent } from '../asset-tag-select/asset-tag-select.component';

@Component({
  selector: 'app-asset-tags',
  templateUrl: './asset-tags.component.html',
  styleUrls: ['./asset-tags.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ReactiveFormsModule, AssetTagListComponent, AssetTagSelectComponent],
})
export class AssetTagsComponent {
  private tagService = inject(TagService);
  private assetService = inject(AssetService);
  private assetTagService = inject(AssetTagService);
  private state = inject(StateService);
  private dialog = inject(DialogService);
  private permissionService = inject(PermissionService);

  readonly asset = input<AssetDto>(undefined);
  readonly gallery = input<GalleryDto>(undefined);

  private readonly assetWithTags = toSignal(
    combineLatest([
      toObservable(this.asset).pipe(filter(Boolean), distinctUntilKeyChanged('id')),
      toObservable(this.gallery).pipe(filter(Boolean), distinctUntilKeyChanged('id')),
    ]).pipe(switchMap(([asset, gallery]) => this.assetService.getAsset(asset.id, gallery.id, { includeTags: true })))
  );

  allTags = toSignal(this.tagService.tags$);

  assetTags = computed(() => {
    const allTags = this.allTags();
    const asset = this.assetWithTags();
    if (!allTags || !asset) return [];
    return allTags.filter(tag => {
      if (asset.tags.some(at => at.id === tag.id)) return true;
      return false;
    });
  });

  canCreate = computed(() => {
    const allTags = this.allTags();
    const gallery = this.gallery();
    if (!allTags || !gallery) return false;
    const tenantId = (gallery.tenant as TenantDto)?.id || (gallery.tenant as string);
    if (!this.state.tenants?.some(t => t.id === tenantId)) return false;
    if (!this.permissionService.hasRolePermissionLegacy('tags:create')) return false;
    return true;
  });

  addTag(name: string, category: string) {
    return this.tagService.tags$
      .pipe(
        take(1),
        tap(tags => {
          const existingTag = tags.find(t => t.name === name);
          if (existingTag) return this.addTagToAsset(existingTag);
          this.tagService.createTag(name, category).subscribe(tag => {
            this.addTagToAsset(tag);
          });
        })
      )
      .subscribe();
  }

  addTagToAsset(tag: Tag) {
    const asset = this.assetWithTags();
    if (!asset.tags.some(t => t.id === tag.id)) {
      this.assetTagService.add(asset, tag).subscribe();
    }
  }

  removeTagFromAsset(tag: Tag, event: Event) {
    event.stopPropagation();
    this.assetTagService.remove(this.assetWithTags(), tag).subscribe();
  }

  canEditTag() {
    const gallery = this.gallery();
    const tenantId = (gallery.tenant as TenantDto)?.id || (gallery.tenant as string);
    if (!this.state.tenants?.some(t => t.id === tenantId)) return false;
    if (!this.permissionService.hasRolePermissionLegacy('tags:update')) return false;
    return true;
  }

  editTag(tag: Tag) {
    if (!this.canEditTag) return;
    this.dialog.open(EditTagDialogComponent, { tag });
  }

  isFocused(el: HTMLElement, event: MouseEvent) {
    event.stopPropagation();
    return el === document.activeElement;
  }
}
